import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, FolderOpen, Users, Newspaper,
  Plus, Edit, Trash2, Eye, LogOut, BarChart3, TrendingUp, Settings, Shield,
  Search, X, Clock, Camera, BookOpen, Bell, CheckSquare, AlertCircle, Bot, DollarSign,
  Cog, ChevronDown, ChevronRight
} from 'lucide-react';
import { useAuth, useNewspaper } from '../context/AppContext';
import { articleService, categoryService, authorService, editionService, configService, userAdminService, storageService } from '../services/api';
import AssistantTab from '../components/admin/AssistantTab';
import PublicidadTab from '../components/admin/publicidad/PublicidadTab';
import CategoriesGrid from '../components/admin/CategoriesGrid';
import ChatWidget from '../components/ChatWidget';
import ThemeSwitcher from '../components/layout/ThemeSwitcher';
import PlantillasEditorPage from '../components/maqueta/PlantillasEditorPage';
import BibliotecaMultimediaPage from './BibliotecaMultimediaPage';
import styles from './AdminPage.module.css';

export default function AdminPage() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated, newsRoles, canAccessAdmin, hasNewsRole } = useAuth();
  const { config, refreshData, departamentos } = useNewspaper();

  const [activeTab, setActiveTab] = useState('dashboard');

  // Sidebar — dos grupos colapsables mutex: Operación y Administración.
  // Solo uno puede estar expandido a la vez; abrir uno colapsa el otro.
  // Operación arranca abierta por defecto porque es el trabajo diario.
  const operacionTabs = ['articles', 'categories', 'editions', 'revisiones', 'biblioteca', 'publicidad'];
  const adminTabs     = ['authors', 'users', 'periodico', 'editorial', 'plantillas'];
  const [operacionOpen, setOperacionOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);
  useEffect(() => {
    if (operacionTabs.includes(activeTab)) { setOperacionOpen(true); setAdminOpen(false); }
    else if (adminTabs.includes(activeTab)) { setAdminOpen(true); setOperacionOpen(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors]       = useState([]);
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ articles: 0, categories: 0, editions: 0, views: 0 });

  // Filtros del listado de artículos (server-side)
  const [artFilters, setArtFilters] = useState({
    q: '', category: '', author: '', estado: '',
  });

  // Form state para editar los datos del periódico.
  // Se rellena desde 'config' cuando llega del backend.
  const [periodicoForm, setPeriodicoForm] = useState({
    name: '', description: '', primary: '', secondary: '',
  });
  const [periodicoSaving, setPeriodicoSaving] = useState(false);
  const [periodicoMsg, setPeriodicoMsg] = useState(null);

  // Estado del tab "Usuarios y roles"
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  // Modal "ver ediciones del artículo". Guarda el artículo seleccionado
  // y la lista de ediciones donde está vinculado.
  const [articleForEditions, setArticleForEditions] = useState(null);
  const [articleEditions, setArticleEditions] = useState([]);
  const [loadingEditions, setLoadingEditions] = useState(false);
  const [userHistory, setUserHistory] = useState([]);
  const [userRegions, setUserRegions] = useState([]);
  const [regionToAssign, setRegionToAssign] = useState('');
  const [regionSaving, setRegionSaving] = useState(false);
  const [roleToAssign, setRoleToAssign] = useState('Redactor');
  const [userSaving, setUserSaving] = useState(false);
  const ROLES_DISPONIBLES = ['EditorNacional', 'Editor', 'Redactor', 'Colaborador', 'SoloLectura'];

  // Estado del tab "Autores"
  const [authorSaving, setAuthorSaving] = useState(false);
  const [linkingAuthor, setLinkingAuthor] = useState(null); // { authorId }
  const [candidateUsers, setCandidateUsers] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [editingAuthor, setEditingAuthor] = useState(null); // objeto con datos
  const [avatarFile, setAvatarFile] = useState(null);        // File seleccionado (opcional)
  const [avatarPreview, setAvatarPreview] = useState(null);  // URL local del preview
  const emptyAuthorForm = {
    name: '', slug: '', bio: '', avatar: '', email: '', celular: '',
    twitter: '', instagram: '', linkedin: '', website: '',
  };

  // Estado del tab "Parámetros editoriales" (Fase A+B+C)
  const [editorialCfg, setEditorialCfg] = useState(null);
  const [editorialSaving, setEditorialSaving] = useState(false);
  const [editorialMsg, setEditorialMsg] = useState(null);
  const PERIODICIDADES = [
    { value: 'diaria',     label: 'Diaria' },
    { value: 'semanal',    label: 'Semanal' },
    { value: 'quincenal',  label: 'Quincenal' },
    { value: 'mensual',    label: 'Mensual' },
    { value: 'ocasional',  label: 'Ocasional' },
  ];
  const FORMATOS_NUM = [
    { value: 'secuencial', label: 'Secuencial (1, 2, 3…)' },
    { value: 'por_ano',    label: 'Por año (2026-001, 2026-002…)' },
    { value: 'por_fecha',  label: 'Por fecha (20260820)' },
  ];
  const ZONAS_HORARIAS = [
    'America/Bogota',
    'America/Mexico_City',
    'America/Lima',
    'America/Santiago',
    'America/Caracas',
    'America/Panama',
    'America/Guatemala',
    'America/New_York',
    'Europe/Madrid',
    'UTC',
  ];

  // Estado del tab "Revisiones pendientes"
  const [pendientes, setPendientes] = useState([]);
  const [pendientesLoading, setPendientesLoading] = useState(false);

  useEffect(() => {
    if (config) {
      setPeriodicoForm({
        name:        config.name || '',
        description: config.description || '',
        primary:     config.colors?.primary || '',
        secondary:   config.colors?.secondary || '',
      });
    }
  }, [config]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadData();
  }, [isAuthenticated, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [articlesData, categoriesData, authorsData, editionsData] = await Promise.all([
        articleService.getAllAdmin({ limit: 100 }),
        categoryService.getAll(),
        authorService.getAll(),
        editionService.getAllAdmin()
      ]);

      setArticles(articlesData.data || []);
      setCategories(categoriesData || []);
      setAuthors(authorsData || []);
      setEditions(editionsData?.data || editionsData || []);

      setStats({
        articles: articlesData.total || articlesData.data?.length || 0,
        categories: categoriesData?.length || 0,
        editions: editionsData?.length || 0,
        views: (articlesData.data || []).reduce((sum, a) => sum + (a.views || 0), 0)
      });
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteArticle = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este artículo?')) {
      try {
        await articleService.delete(id);
        setArticles(articles.filter(a => a.id !== id));
      } catch (error) {
        alert('Error al eliminar');
      }
    }
  };

  // Abre el modal que muestra en qué ediciones aparece este artículo.
  // Lo lanza el botón con icono de periódico en la columna Acciones.
  const openArticleEditions = async (article) => {
    setArticleForEditions(article);
    setArticleEditions([]);
    setLoadingEditions(true);
    try {
      const list = await articleService.listEditions(article.id);
      setArticleEditions(list || []);
    } catch (err) {
      console.error('Error cargando ediciones del artículo:', err);
      setArticleEditions([]);
    } finally {
      setLoadingEditions(false);
    }
  };

  const closeArticleEditions = () => {
    setArticleForEditions(null);
    setArticleEditions([]);
  };

  // Recarga solo la lista de artículos aplicando los filtros actuales.
  // Se dispara al cambiar cualquier filtro (con un pequeño debounce para
  // el input de búsqueda).
  const reloadArticles = async (filters) => {
    try {
      const data = await articleService.getAllAdmin({
        limit: 100,
        search:   filters.q      || undefined,
        category: filters.category || undefined,
        author:   filters.author   || undefined,
        estado:   filters.estado   || undefined,
      });
      setArticles(data.data || []);
    } catch (err) {
      console.error('Error recargando artículos:', err);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setTimeout(() => reloadArticles(artFilters), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artFilters]);

  // ─── Usuarios y roles ────────────────────────────────────────────
  const loadUsers = async (search = '') => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const data = await userAdminService.list({ search });
      setUsers(data || []);
    } catch (err) {
      setUsersError('Error al cargar usuarios: ' + err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  // Cargar la lista la primera vez que se abre el tab de usuarios.
  useEffect(() => {
    if (activeTab === 'users' && users.length === 0 && !usersLoading) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const openUserModal = async (u) => {
    setSelectedUser(u);
    setUserHistory([]);
    setUserRegions([]);
    try {
      const [h, r] = await Promise.all([
        userAdminService.getHistory(u.id).catch(() => []),
        userAdminService.listRegions(u.id).catch(() => []),
      ]);
      setUserHistory(h || []);
      setUserRegions(r || []);
    } catch (err) {
      console.error('Error cargando detalle usuario:', err);
    }
  };

  const closeUserModal = () => {
    setSelectedUser(null);
    setUserHistory([]);
    setUserRegions([]);
    setRegionToAssign('');
  };

  const assignRegion = async () => {
    if (!selectedUser || !regionToAssign) return;
    const dep = (departamentos || []).find(d =>
      String(d.id) === String(regionToAssign));
    if (!dep) return;
    setRegionSaving(true);
    try {
      await userAdminService.assignRegion(selectedUser.id, dep.id, dep.nombre);
      const r = await userAdminService.listRegions(selectedUser.id);
      setUserRegions(r || []);
      setRegionToAssign('');
    } catch (err) {
      alert('No se pudo asignar el departamento: ' + err.message);
    } finally {
      setRegionSaving(false);
    }
  };

  const revokeRegion = async (departmentId, departmentName) => {
    if (!selectedUser) return;
    if (!window.confirm(`¿Quitar el departamento ${departmentName || departmentId} a ${selectedUser.nombre}?`)) return;
    setRegionSaving(true);
    try {
      await userAdminService.revokeRegion(selectedUser.id, departmentId);
      const r = await userAdminService.listRegions(selectedUser.id);
      setUserRegions(r || []);
    } catch (err) {
      alert('No se pudo quitar el departamento: ' + err.message);
    } finally {
      setRegionSaving(false);
    }
  };

  const assignRole = async () => {
    if (!selectedUser || !roleToAssign) return;
    setUserSaving(true);
    try {
      await userAdminService.assignRole(selectedUser.id, roleToAssign);
      const [detail, h] = await Promise.all([
        userAdminService.getById(selectedUser.id),
        userAdminService.getHistory(selectedUser.id),
      ]);
      setSelectedUser(detail);
      setUserHistory(h || []);
      await loadUsers(userSearch);
    } catch (err) {
      alert('No se pudo asignar el rol: ' + err.message);
    } finally {
      setUserSaving(false);
    }
  };

  const revokeRole = async (rol) => {
    if (!selectedUser) return;
    if (!window.confirm(`¿Revocar el rol ${rol} a ${selectedUser.nombre}?`)) return;
    setUserSaving(true);
    try {
      await userAdminService.revokeRole(selectedUser.id, rol);
      const [detail, h] = await Promise.all([
        userAdminService.getById(selectedUser.id),
        userAdminService.getHistory(selectedUser.id),
      ]);
      setSelectedUser(detail);
      setUserHistory(h || []);
      await loadUsers(userSearch);
    } catch (err) {
      alert('No se pudo revocar el rol: ' + err.message);
    } finally {
      setUserSaving(false);
    }
  };

  // ─── Autores ────────────────────────────────────────────────────
  const reloadAuthors = async () => {
    try {
      const list = await authorService.getAll();
      setAuthors(list || []);
    } catch (err) {
      console.error('Error recargando autores:', err);
    }
  };

  const openLinkUserModal = async (author) => {
    setLinkingAuthor(author);
    setCandidateSearch('');
    try {
      const list = await userAdminService.list({ search: '' });
      setCandidateUsers(list || []);
    } catch (err) {
      setCandidateUsers([]);
    }
  };

  const closeLinkUserModal = () => {
    setLinkingAuthor(null);
    setCandidateUsers([]);
    setCandidateSearch('');
  };

  const linkUserToAuthor = async (usuarioId) => {
    if (!linkingAuthor) return;
    setAuthorSaving(true);
    try {
      await authorService.linkUser(linkingAuthor.id, usuarioId);
      await reloadAuthors();
      closeLinkUserModal();
    } catch (err) {
      alert('No se pudo vincular: ' + err.message);
    } finally {
      setAuthorSaving(false);
    }
  };

  const unlinkUserFromAuthor = async (author) => {
    if (!window.confirm(`¿Desvincular al usuario de "${author.name}"? El autor queda como firma standalone.`)) return;
    setAuthorSaving(true);
    try {
      await authorService.unlinkUser(author.id);
      await reloadAuthors();
    } catch (err) {
      alert('No se pudo desvincular: ' + err.message);
    } finally {
      setAuthorSaving(false);
    }
  };

  // Crear autor desde el usuario abierto en el modal
  const createAuthorFromUser = async () => {
    if (!selectedUser) return;
    setUserSaving(true);
    try {
      await authorService.createFromUser(selectedUser.id);
      await reloadAuthors();
      // Recargar el detalle del usuario para reflejar el vínculo
      const detail = await userAdminService.getById(selectedUser.id);
      setSelectedUser(detail);
    } catch (err) {
      alert('No se pudo crear el autor: ' + err.message);
    } finally {
      setUserSaving(false);
    }
  };

  // ─── Crear/editar autor standalone (sin usuario del sistema) ───
  const openAuthorEditor = (author = null) => {
    // Si author es null → creación. Si trae datos → edición.
    setEditingAuthor(author ? { ...author } : { ...emptyAuthorForm });
    setAvatarFile(null);
    setAvatarPreview(null);
  };

  const closeAuthorEditor = () => {
    setEditingAuthor(null);
    setAvatarFile(null);
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarPreview(null);
  };

  // Cuando el usuario selecciona un archivo, generamos preview local
  // sin subir aún (la subida ocurre en saveAuthor tras validar).
  const onAvatarFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Selecciona un archivo de imagen (JPG, PNG, WebP…).');
      return;
    }
    // Límite razonable: 5 MB
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen es muy grande. Máximo 5 MB.');
      return;
    }
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  // Genera slug a partir del nombre. Si el user ya lo tocó, no lo pisa.
  const slugify = (text) => text
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // sin acentos
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const setAuthorField = (field, value) => {
    setEditingAuthor(prev => {
      const next = { ...prev, [field]: value };
      // Auto-slug solo si estamos creando y el usuario NO ha tocado slug.
      if (field === 'name' && !prev.id && !prev._slugTouched) {
        next.slug = slugify(value);
      }
      if (field === 'slug') next._slugTouched = true;
      return next;
    });
  };

  const saveAuthor = async () => {
    if (!editingAuthor) return;
    if (!editingAuthor.name || !editingAuthor.name.trim()) {
      alert('El nombre es obligatorio.');
      return;
    }
    // Al crear un autor: email y celular son obligatorios.
    // Al editar uno existente: solo se exige si ya los tenía (no permitir vaciar).
    const esNuevo = !editingAuthor.id;
    const emailVacio   = !editingAuthor.email    || !String(editingAuthor.email).trim();
    const celularVacio = !editingAuthor.celular  || !String(editingAuthor.celular).trim();
    if (esNuevo && emailVacio) {
      alert('El email de contacto es obligatorio.');
      return;
    }
    if (esNuevo && celularVacio) {
      alert('El teléfono celular es obligatorio.');
      return;
    }
    setAuthorSaving(true);
    try {
      let autorGuardado;

      if (editingAuthor.id) {
        // ─── EDICIÓN ───
        // Si eligieron archivo nuevo, súbelo primero (ya tenemos ID).
        let avatarUrl = editingAuthor.avatar;
        if (avatarFile) {
          const { url } = await storageService.uploadAuthorAvatar(editingAuthor.id, avatarFile);
          avatarUrl = url;
        }
        autorGuardado = await authorService.update(editingAuthor.id, {
          ...editingAuthor,
          avatar: avatarUrl,
        });
      } else {
        // ─── CREACIÓN ───
        // Primero creamos sin avatar, para obtener el ID.
        autorGuardado = await authorService.create(editingAuthor);
        // Si eligieron foto, ahora sí subimos y actualizamos.
        if (avatarFile && autorGuardado?.id) {
          const { url } = await storageService.uploadAuthorAvatar(autorGuardado.id, avatarFile);
          autorGuardado = await authorService.update(autorGuardado.id, {
            ...editingAuthor,
            ...autorGuardado,
            avatar: url,
          });
        }
      }

      await reloadAuthors();
      closeAuthorEditor();
    } catch (err) {
      alert('No se pudo guardar el autor: ' + err.message);
    } finally {
      setAuthorSaving(false);
    }
  };

  const deleteAuthor = async (author) => {
    if (!window.confirm(
      `¿Eliminar al autor "${author.name}"? ` +
      `Los artículos firmados por él perderán la firma.`
    )) return;
    setAuthorSaving(true);
    try {
      await authorService.delete(author.id);
      await reloadAuthors();
    } catch (err) {
      alert('No se pudo eliminar: ' + err.message);
    } finally {
      setAuthorSaving(false);
    }
  };

  // Guardrail: si el usuario cambia a un tab restringido (por URL,
  // por localStorage viejo, por manipulación en devtools), forzar
  // uno permitido. El backend igual rechaza sus acciones, pero UX
  // limpia. Se aplica también al PRIMER render: un Colaborador que
  // abra /admin arranca en 'articles' porque no puede ver dashboard.
  useEffect(() => {
    if (!Array.isArray(newsRoles)) return;
    const soloEditor = ['categories', 'editions', 'users', 'periodico', 'editorial', 'revisiones'];
    const editorORedactor = ['authors', 'dashboard'];

    let fallback = null;
    if (soloEditor.includes(activeTab) && !hasNewsRole('Editor')) {
      fallback = (hasNewsRole('Redactor') || hasNewsRole('Colaborador')) ? 'articles' : 'ayuda';
    } else if (editorORedactor.includes(activeTab)
               && !hasNewsRole('Editor') && !hasNewsRole('Redactor')) {
      fallback = 'articles';
    }
    if (fallback) setActiveTab(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, newsRoles]);
  const loadEditorial = async () => {
    try {
      const cfg = await configService.getEditorial();
      setEditorialCfg(cfg);
    } catch (err) {
      setEditorialMsg({ ok: false, text: 'Error al cargar configuración: ' + err.message });
    }
  };

  useEffect(() => {
    if (activeTab === 'editorial' && !editorialCfg) {
      loadEditorial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const saveEditorial = async () => {
    if (!editorialCfg) return;
    setEditorialSaving(true);
    setEditorialMsg(null);
    try {
      const updated = await configService.updateEditorial({
        periodicidad:         editorialCfg.periodicidad,
        formatoNumeracion:    editorialCfg.formatoNumeracion,
        zonaHoraria:          editorialCfg.zonaHoraria,
        idioma:               editorialCfg.idioma,
        requiereAprobacion:   editorialCfg.requiereAprobacion,
        modoMantenimiento:    editorialCfg.modoMantenimiento,
        mensajeMantenimiento: editorialCfg.mensajeMantenimiento || null,
        // Director: enviar el id si hay uno, o la bandera limpiarDirector
        // si el usuario lo desasignó (select vacío).
        directorAutorId:      editorialCfg.directorAutorId || null,
        limpiarDirector:      !editorialCfg.directorAutorId,
      });
      setEditorialCfg(updated);
      setEditorialMsg({ ok: true, text: 'Configuración guardada.' });
    } catch (err) {
      setEditorialMsg({ ok: false, text: 'Error al guardar: ' + err.message });
    } finally {
      setEditorialSaving(false);
    }
  };

  // ─── Revisiones pendientes ──────────────────────────────────────
  const loadPendientes = async () => {
    setPendientesLoading(true);
    try {
      const paged = await articleService.getRevisionesPendientes({ limit: 50 });
      setPendientes(paged.data || []);
    } catch (err) {
      console.error('Error revisiones:', err);
      setPendientes([]);
    } finally {
      setPendientesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'revisiones') {
      loadPendientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const aprobarArticulo = async (articleId) => {
    if (!window.confirm('¿Aprobar y publicar este artículo?')) return;
    try {
      await articleService.changeStatus(articleId, 'publicado');
      await loadPendientes();
    } catch (err) {
      alert('No se pudo aprobar: ' + err.message);
    }
  };

  const rechazarArticulo = async (articleId) => {
    if (!window.confirm('¿Devolver a borrador para que el autor lo revise?')) return;
    try {
      await articleService.changeStatus(articleId, 'borrador');
      await loadPendientes();
    } catch (err) {
      alert('No se pudo rechazar: ' + err.message);
    }
  };

  // Mapea el enum del backend a etiqueta humana.
  const estadoLabel = (s) => ({
    publicado:   'Publicado',
    borrador:    'Borrador',
    en_revision: 'En revisión',
    archivado:   'Archivado',
  })[s] || (s || '—');

  // Guardar cambios del periódico. El backend actualiza y devuelve el
  // periódico ya modificado. Refrescamos el contexto global para que
  // el header del sitio muestre el nombre nuevo inmediatamente.
  const handleSavePeriodico = async (e) => {
    e.preventDefault();
    setPeriodicoSaving(true);
    setPeriodicoMsg(null);
    try {
      await configService.update({
        name:        periodicoForm.name,
        description: periodicoForm.description,
        // Se conservan los otros campos actuales:
        domain: config?.domain,
        logo:   config?.logo,
        colors: {
          primary:   periodicoForm.primary   || config?.colors?.primary,
          secondary: periodicoForm.secondary || config?.colors?.secondary,
        },
      });
      if (refreshData) await refreshData();
      setPeriodicoMsg({ ok: true, text: 'Guardado correctamente.' });
    } catch (err) {
      setPeriodicoMsg({ ok: false, text: err.message || 'Error al guardar.' });
    } finally {
      setPeriodicoSaving(false);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  // Sin roles internos de News: el usuario está autorizado a la app
  // (aud del JWT incluye 'news') pero un Editor aún no le asignó
  // ningún rol interno. No puede hacer nada útil en el admin.
  if (newsRoles !== null && !canAccessAdmin) {
    return (
      <div style={{
        minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{
          maxWidth: 520, background: '#fff', padding: 40, borderRadius: 8,
          textAlign: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#fff5e6', color: '#a15c00',
            margin: '0 auto 20px', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <Shield size={32} />
          </div>
          <h1 style={{ marginTop: 0, fontFamily: 'var(--font-serif)' }}>
            Sin acceso al panel
          </h1>
          <p style={{ color: '#555', lineHeight: 1.6 }}>
            Tu cuenta está autorizada para usar Comercio Colaborativo pero
            aún no tiene un rol asignado en el sistema.
          </p>
          <p style={{ color: '#555', lineHeight: 1.6 }}>
            Contacta al <strong>Editor</strong> del periódico para que te
            asigne uno de estos roles: <em>Editor</em>, <em>Redactor</em>,
            {' '}<em>Colaborador</em> o <em>Solo lectura</em>.
          </p>
          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
            <button
              onClick={handleLogout}
              className={styles.addBtn}
            >
              Cerrar sesión
            </button>
            <span style={{ fontSize: 12, color: '#888' }}>
              Al cerrar sesión volverás al sitio como visitante.
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.adminPage}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h2>Panel Admin</h2>
          <span>{user?.name}</span>
          {/* Rol(es) actual(es) del usuario en News. Se muestra siempre
              para que el usuario sepa qué puede/no puede hacer. */}
          {Array.isArray(newsRoles) && newsRoles.length > 0 && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {newsRoles.map(r => (
                <span key={r} style={{
                  padding: '2px 8px',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '.03em',
                  textTransform: 'uppercase',
                  background: 'var(--color-accent, #c9a227)',
                  color: '#1a1a1a',
                }}>{r}</span>
              ))}
            </div>
          )}
        </div>
        
        <nav className={styles.sidebarNav}>
          {/* Dashboard: Editor y Redactor. Al Colaborador le sale vacío
              porque los stats son globales del periódico, así que
              directamente no se muestra. */}
          {(hasNewsRole('Editor') || hasNewsRole('Redactor')) && (
            <button 
              className={`${styles.navItem} ${activeTab === 'dashboard' ? styles.active : ''}`}
              onClick={() => setActiveTab('dashboard')}
            >
              <LayoutDashboard size={20} />
              Dashboard
            </button>
          )}

          {/* ═══ Grupo: Operación ══════════════════════════════════════════
              Trabajo editorial del día a día. Agrupa Artículos, Categorías,
              Ediciones, Revisiones pendientes, Biblioteca Multimedia y
              Publicidad. Se auto-expande si algún tab del grupo está activo.
              Solo uno de los grupos (Operación / Administración) puede estar
              expandido a la vez — abrir uno colapsa el otro. */}
          <>
            <button
              className={styles.navItem}
              onClick={() => { setOperacionOpen(o => !o); if (!operacionOpen) setAdminOpen(false); }}
              aria-expanded={operacionOpen}
            >
              <FileText size={20} />
              Operación
              <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                {operacionOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </button>

            {operacionOpen && (
              <div style={{ paddingLeft: 12 }}>
                {/* Artículos: todos los roles (Colaborador solo verá los suyos) */}
                <button 
                  className={`${styles.navItem} ${activeTab === 'articles' ? styles.active : ''}`}
                  onClick={() => setActiveTab('articles')}
                >
                  <FileText size={20} />
                  Artículos
                </button>

                {/* Categorías: solo Editor */}
                {hasNewsRole('Editor') && (
                  <button 
                    className={`${styles.navItem} ${activeTab === 'categories' ? styles.active : ''}`}
                    onClick={() => setActiveTab('categories')}
                  >
                    <FolderOpen size={20} />
                    Categorías
                  </button>
                )}

                {/* Ediciones: solo Editor */}
                {hasNewsRole('Editor') && (
                  <button 
                    className={`${styles.navItem} ${activeTab === 'editions' ? styles.active : ''}`}
                    onClick={() => setActiveTab('editions')}
                  >
                    <Newspaper size={20} />
                    Ediciones
                  </button>
                )}

                {/* Revisiones pendientes: solo Editor */}
                {hasNewsRole('Editor') && (
                  <button
                    className={`${styles.navItem} ${activeTab === 'revisiones' ? styles.active : ''}`}
                    onClick={() => setActiveTab('revisiones')}
                  >
                    <CheckSquare size={20} />
                    Revisiones pendientes
                  </button>
                )}

                {/* Biblioteca Multimedia */}
                <button
                  className={`${styles.navItem} ${activeTab === 'biblioteca' ? styles.active : ''}`}
                  onClick={() => setActiveTab('biblioteca')}
                >
                  <Camera size={20} />
                  Biblioteca Multimedia
                </button>

                {/* Publicidad */}
                <button
                  className={`${styles.navItem} ${activeTab === 'publicidad' ? styles.active : ''}`}
                  onClick={() => setActiveTab('publicidad')}
                >
                  <DollarSign size={20} />
                  Publicidad
                </button>
              </div>
            )}
          </>

          {/* ═══ Grupo: Administración ═══════════════════════════════════
              Configuración del periódico y su staff. Autores, Usuarios y
              roles, Periódico y Parámetros editoriales. Mismo mutex que
              Operación. */}
          {(hasNewsRole('Editor') || hasNewsRole('Redactor')) && (
            <>
              <button
                className={styles.navItem}
                onClick={() => { setAdminOpen(o => !o); if (!adminOpen) setOperacionOpen(false); }}
                aria-expanded={adminOpen}
              >
                <Cog size={20} />
                Administración
                <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
                  {adminOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </span>
              </button>

              {adminOpen && (
                <div style={{ paddingLeft: 12 }}>
                  {/* Autores: Editor y Redactor */}
                  {(hasNewsRole('Editor') || hasNewsRole('Redactor')) && (
                    <button
                      className={`${styles.navItem} ${activeTab === 'authors' ? styles.active : ''}`}
                      onClick={() => setActiveTab('authors')}
                    >
                      <Users size={20} />
                      Autores
                    </button>
                  )}

                  {/* Usuarios y roles: solo Editor */}
                  {hasNewsRole('Editor') && (
                    <button
                      className={`${styles.navItem} ${activeTab === 'users' ? styles.active : ''}`}
                      onClick={() => setActiveTab('users')}
                    >
                      <Shield size={20} />
                      Usuarios y roles
                    </button>
                  )}

                  {/* Periódico (config visual): solo Editor */}
                  {hasNewsRole('Editor') && (
                    <button
                      className={`${styles.navItem} ${activeTab === 'periodico' ? styles.active : ''}`}
                      onClick={() => setActiveTab('periodico')}
                    >
                      <Settings size={20} />
                      Periódico
                    </button>
                  )}

                  {/* Parámetros editoriales: solo Editor */}
                  {hasNewsRole('Editor') && (
                    <button
                      className={`${styles.navItem} ${activeTab === 'editorial' ? styles.active : ''}`}
                      onClick={() => setActiveTab('editorial')}
                    >
                      <AlertCircle size={20} />
                      Parámetros editoriales
                    </button>
                  )}

                  {/* Plantillas de página (maqueta impresa): Editor y EditorNacional */}
                  {hasNewsRole('Editor') && (
                    <button
                      className={`${styles.navItem} ${activeTab === 'plantillas' ? styles.active : ''}`}
                      onClick={() => setActiveTab('plantillas')}
                    >
                      <LayoutDashboard size={20} />
                      Plantillas de página
                    </button>
                  )}
                </div>
              )}
            </>
          )}

          {/* Asistente: chatbot de ayuda basado en el manual de usuario.
              Visible para todos los roles internos (no requiere permisos
              extra; ni siquiera hace llamadas al backend). */}
          <button
            className={`${styles.navItem} ${activeTab === 'asistente' ? styles.active : ''}`}
            onClick={() => setActiveTab('asistente')}
          >
            <Bot size={20} />
            Asistente
          </button>

          {/* Ayuda: todos */}
          <button
            className={`${styles.navItem} ${activeTab === 'ayuda' ? styles.active : ''}`}
            onClick={() => setActiveTab('ayuda')}
          >
            <BookOpen size={20} />
            Ayuda y reglas
          </button>
        </nav>

        <div className={styles.sidebarFooter}>
          {/* Selector de tema visual — al lado de Ver sitio, como pediste.
              Reutiliza los estilos navItem para verse igual que Ver sitio /
              Cerrar sesión; el panel se despliega hacia arriba. */}
          <ThemeSwitcher
            variant="inline"
            panelUp
            triggerClassName={styles.navItem}
            iconSize={20}
          />
          <Link to="/" className={styles.navItem}>
            <Eye size={20} />
            Ver sitio
          </Link>
          <button className={styles.navItem} onClick={handleLogout}>
            <LogOut size={20} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={styles.mainContent}>
        {loading ? (
          <div className={styles.loading}>Cargando...</div>
        ) : (
          <>
            {/* Dashboard */}
            {activeTab === 'dashboard' && (
              <div className={styles.dashboard}>
                <h1>Dashboard</h1>
                
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <FileText size={32} />
                    <div>
                      <span className={styles.statNumber}>{stats.articles}</span>
                      <span className={styles.statLabel}>Artículos</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <FolderOpen size={32} />
                    <div>
                      <span className={styles.statNumber}>{stats.categories}</span>
                      <span className={styles.statLabel}>Categorías</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <Newspaper size={32} />
                    <div>
                      <span className={styles.statNumber}>{stats.editions}</span>
                      <span className={styles.statLabel}>Ediciones</span>
                    </div>
                  </div>
                  <div className={styles.statCard}>
                    <TrendingUp size={32} />
                    <div>
                      <span className={styles.statNumber}>{stats.views.toLocaleString()}</span>
                      <span className={styles.statLabel}>Vistas totales</span>
                    </div>
                  </div>
                </div>

                <div className={styles.recentSection}>
                  <h2>Artículos recientes</h2>
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Título</th>
                          <th>Categoría</th>
                          <th>Vistas</th>
                          <th>Creado</th>
                          <th>Publicado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articles.slice(0, 5).map(article => (
                          <tr key={article.id}>
                            <td>{article.title}</td>
                            <td>{article.category?.name || '-'}</td>
                            <td>{article.views}</td>
                            <td>{article.createdAt ? new Date(article.createdAt).toLocaleDateString() : '—'}</td>
                            <td>{article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Artículos */}
            {activeTab === 'articles' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Artículos</h1>
                  <Link to="/admin/articulo/nuevo" className={styles.addBtn}>
                    <Plus size={18} />
                    Nuevo artículo
                  </Link>
                </div>

                {/* Filtros del listado */}
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' }}>
                  <input
                    type="search"
                    value={artFilters.q}
                    onChange={e => setArtFilters(f => ({ ...f, q: e.target.value }))}
                    placeholder="Buscar título, subtítulo, cuerpo…"
                    style={{ flex: '1 1 260px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                  <select
                    value={artFilters.category}
                    onChange={e => setArtFilters(f => ({ ...f, category: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                  >
                    <option value="">Todas las categorías</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.slug}>{c.name}</option>
                    ))}
                  </select>
                  <select
                    value={artFilters.author}
                    onChange={e => setArtFilters(f => ({ ...f, author: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                  >
                    <option value="">Todos los autores</option>
                    {authors.map(a => (
                      <option key={a.id} value={a.slug}>{a.name}</option>
                    ))}
                  </select>
                  <select
                    value={artFilters.estado}
                    onChange={e => setArtFilters(f => ({ ...f, estado: e.target.value }))}
                    style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6 }}
                  >
                    <option value="">Todos los estados</option>
                    <option value="publicado">Publicado</option>
                    <option value="borrador">Borrador</option>
                    <option value="en_revision">En revisión</option>
                    <option value="archivado">Archivado</option>
                  </select>
                  {(artFilters.q || artFilters.category || artFilters.author || artFilters.estado) && (
                    <button
                      type="button"
                      onClick={() => setArtFilters({ q: '', category: '', author: '', estado: '' })}
                      style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: 'transparent', cursor: 'pointer' }}
                    >
                      Limpiar
                    </button>
                  )}
                </div>

                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Título</th>
                        <th>Categoría</th>
                        <th>Autor</th>
                        <th>Estado</th>
                        <th>Vistas</th>
                        <th>Creado</th>
                        <th>Publicado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {articles.length === 0 && (
                        <tr>
                          <td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
                            No hay artículos que coincidan con los filtros.
                          </td>
                        </tr>
                      )}
                      {articles.map(article => (
                        <tr key={article.id}>
                          <td>{article.title}</td>
                          <td>{article.category?.name || '-'}</td>
                          <td>{article.author?.name || '-'}</td>
                          <td>
                            <span className={`${styles.status} ${styles[article.status]}`}>
                              {estadoLabel(article.status)}
                            </span>
                          </td>
                          <td>{article.views}</td>
                          <td>{article.createdAt ? new Date(article.createdAt).toLocaleDateString() : '—'}</td>
                          <td>{article.publishedAt ? new Date(article.publishedAt).toLocaleDateString() : '—'}</td>
                          <td className={styles.actions}>
                            <Link to={`/articulo/${article.slug}`} title="Ver">
                              <Eye size={16} />
                            </Link>
                            <button
                              onClick={() => openArticleEditions(article)}
                              title="Ver ediciones donde aparece"
                              style={{
                                background: 'transparent', border: 'none',
                                cursor: 'pointer', padding: 0, color: '#555',
                                display: 'inline-flex', alignItems: 'center',
                              }}
                            >
                              <Newspaper size={16} />
                            </button>
                            <Link to={`/admin/articulo/editar/${article.id}`} title="Editar">
                              <Edit size={16} />
                            </Link>
                            <button onClick={() => handleDeleteArticle(article.id)} title="Eliminar">
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}


            {/* Categorías */}
            {activeTab === 'categories' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Categorías</h1>
                  {/* <button className={styles.addBtn}>
                    <Plus size={18} />
                    Nueva categoría
                  </button> */}
                  <Link to="/admin/categoria/nueva" className={styles.addBtn}>
                    <Plus size={18} />
                    Nueva categoría
                  </Link>
                </div>

                <CategoriesGrid categories={categories} />
              </div>
            )}

            {/* Ediciones */}
            {activeTab === 'editions' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Ediciones</h1>
                  {/* <button className={styles.addBtn}>
                    <Plus size={18} />
                    Nueva edición
                  </button> */}
                  <Link to="/admin/edicion/nueva" className={styles.addBtn}>
                    <Plus size={18} />
                    Nueva edición
                  </Link>
                </div>

                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Número</th>
                        <th>Título</th>
                        <th>Fecha</th>
                        <th>Estado</th>
                        <th>Artículos</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editions.map(edition => (
                        <tr key={edition.id}>
                          <td>N° {edition.number}</td>
                          <td>{edition.title}</td>
                          <td>{new Date(edition.publishedAt || edition.date).toLocaleDateString()}</td>
                          <td>
                            <span className={`${styles.status} ${styles[edition.status]}`}>
                              {edition.status === 'published' ? 'Publicada' : 'Borrador'}
                            </span>
                          </td>
                          <td>{edition.articles?.length || 0}</td>
                          <td className={styles.actions}>
                            <Link to={`/ediciones/${edition.number}`} title="Ver">
                              <Eye size={16} />
                            </Link>
                            {/* <button title="Editar"><Edit size={16} /></button> */}
                            <Link to={`/admin/edicion/editar/${edition.id}`} title="Editar"><Edit size={16} /></Link>
                            <button title="Eliminar"><Trash2 size={16} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Autores */}
            {activeTab === 'authors' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Autores</h1>
                  <button className={styles.addBtn} onClick={() => openAuthorEditor(null)}>
                    <Plus size={18} />
                    Nuevo autor
                  </button>
                </div>

                {authors.length === 0 ? (
                  <p>No hay autores creados todavía.</p>
                ) : (
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Email público</th>
                          <th>Usuario del sistema</th>
                          <th style={{ textAlign: 'right' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {authors.map(a => (
                          <tr key={a.id}>
                            <td>
                              <strong>{a.name}</strong>
                              {a.bio && (
                                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                  {a.bio.length > 80 ? a.bio.substring(0, 80) + '…' : a.bio}
                                </div>
                              )}
                            </td>
                            <td>{a.email || '—'}</td>
                            <td>
                              {a.userId ? (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: 'var(--color-primary)',
                                  color: '#fff'
                                }}>
                                  Vinculado
                                </span>
                              ) : (
                                <span style={{ color: '#999', fontSize: 12 }}>Standalone</span>
                              )}
                            </td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <button
                                className={styles.iconBtn}
                                onClick={() => openAuthorEditor(a)}
                                disabled={authorSaving}
                                title="Editar autor"
                                style={{ marginRight: 4 }}
                              >
                                <Edit size={14} />
                              </button>
                              {a.userId ? (
                                <button
                                  className={styles.iconBtn}
                                  onClick={() => unlinkUserFromAuthor(a)}
                                  disabled={authorSaving}
                                  title="Desvincular usuario"
                                  style={{ marginRight: 4 }}
                                >
                                  Desvincular
                                </button>
                              ) : (
                                <button
                                  className={styles.iconBtn}
                                  onClick={() => openLinkUserModal(a)}
                                  disabled={authorSaving}
                                  title="Vincular a usuario del sistema"
                                  style={{ marginRight: 4 }}
                                >
                                  Vincular
                                </button>
                              )}
                              <button
                                className={styles.iconBtn}
                                onClick={() => deleteAuthor(a)}
                                disabled={authorSaving}
                                title="Eliminar autor"
                                style={{ color: '#b00020' }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <p style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
                  Los autores son la <strong>firma editorial</strong> que aparece en cada artículo.
                  Vincular un autor con un usuario del sistema permite que ese usuario
                  aparezca automáticamente como opción al firmar sus propios artículos.
                  Los autores standalone (sin usuario) son colaboradores externos que
                  el equipo interno firma en su nombre.
                </p>
              </div>
            )}

            {/* Usuarios y roles — panel de administración del sistema
                de identidad interno de News (ver deuda #1). */}
            {activeTab === 'users' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Usuarios y roles</h1>
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'stretch' }}>
                  <div style={{
                    flex: 1,
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    <Search
                      size={18}
                      style={{
                        position: 'absolute', left: 12, top: '50%',
                        transform: 'translateY(-50%)', color: '#888',
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Buscar por nombre o email..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && loadUsers(userSearch)}
                      style={{
                        width: '100%',
                        padding: '10px 12px 10px 40px',
                        border: '1px solid #ccc',
                        borderRadius: 4,
                        fontSize: 14,
                        background: '#fff',
                      }}
                    />
                  </div>
                  <button
                    className={styles.addBtn}
                    onClick={() => loadUsers(userSearch)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Buscar
                  </button>
                </div>

                {usersError && (
                  <p style={{ color: '#b00020' }}>{usersError}</p>
                )}
                {usersLoading && <p>Cargando…</p>}
                {!usersLoading && users.length === 0 && !usersError && (
                  <div style={{
                    padding: 24, background: '#f0f7ff', border: '1px solid #b8daff',
                    borderRadius: 6, marginTop: 8,
                  }}>
                    <p style={{ marginTop: 0 }}>
                      <strong>No hay usuarios con roles asignados todavía.</strong>
                    </p>
                    <p style={{ margin: '8px 0', color: '#004085' }}>
                      Los usuarios del ecosistema Ecolab aparecen aquí después de que
                      un administrador de Ecolab les da acceso a la app <strong>News</strong> y
                      ellos hacen su primer login en este portal.
                    </p>
                    <p style={{ margin: '8px 0 0 0', color: '#004085', fontSize: 13 }}>
                      Desde News solo asignamos los roles internos
                      (<em>Editor</em>, <em>Redactor</em>, <em>Colaborador</em>, <em>SoloLectura</em>) a
                      quienes ya tienen acceso a la app.
                    </p>
                  </div>
                )}

                {!usersLoading && users.length > 0 && (
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>Roles</th>
                          <th>Último acceso</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map(u => (
                          <tr key={u.id}>
                            <td>{u.nombre || '—'} {u.apellido || ''}</td>
                            <td>{u.email || '—'}</td>
                            <td>
                              {(u.roles || []).length === 0 ? (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: 999,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  background: '#f0f0f0',
                                  color: '#888',
                                  fontStyle: 'italic',
                                }}>Sin rol</span>
                              ) : (
                                (u.roles || []).map(r => (
                                  <span key={r.rol} style={{
                                    display: 'inline-block',
                                    padding: '2px 8px',
                                    marginRight: 4,
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    background: 'var(--color-primary)',
                                    color: '#fff'
                                  }}>{r.rol}</span>
                                ))
                              )}
                            </td>
                            <td>{u.ultimoLogin ? new Date(u.ultimoLogin).toLocaleDateString() : '—'}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {(u.roles || []).length === 0 ? (
                                <button
                                  className={styles.addBtn}
                                  onClick={() => openUserModal(u)}
                                  title="Asignar rol"
                                  style={{ fontSize: 12, padding: '4px 10px' }}
                                >
                                  <Plus size={12} /> Asignar rol
                                </button>
                              ) : (
                                <button
                                  className={styles.iconBtn}
                                  onClick={() => openUserModal(u)}
                                  title="Gestionar"
                                >
                                  <Edit size={16} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Periódico — edición de datos generales del tenant único */}
            {activeTab === 'periodico' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Periódico</h1>
                </div>
                <p style={{ marginTop: 0, color: '#666' }}>
                  Datos generales que se muestran en la cabecera del sitio.
                  La creación / eliminación de periódicos se maneja aparte
                  como parte del multi-tenant del ecosistema.
                </p>

                <form onSubmit={handleSavePeriodico} className={styles.periodicoForm}>
                  <label className={styles.field}>
                    <span>Nombre</span>
                    <input
                      type="text"
                      value={periodicoForm.name}
                      onChange={e => setPeriodicoForm({ ...periodicoForm, name: e.target.value })}
                      placeholder="Ej: Comercio Colaborativo"
                      required
                    />
                  </label>

                  <label className={styles.field}>
                    <span>Descripción / Tagline</span>
                    <input
                      type="text"
                      value={periodicoForm.description}
                      onChange={e => setPeriodicoForm({ ...periodicoForm, description: e.target.value })}
                      placeholder="Ej: Su vitrina comercial al consumo colaborativo inteligente"
                    />
                  </label>

                  <div className={styles.fieldRow}>
                    <label className={styles.field}>
                      <span>Color primario</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <input
                          type="color"
                          value={/^#[0-9a-fA-F]{6}$/.test(periodicoForm.primary)
                                 ? periodicoForm.primary : '#1B4332'}
                          onChange={e => setPeriodicoForm({
                            ...periodicoForm, primary: e.target.value.toUpperCase(),
                          })}
                          title="Elegir color"
                          style={{
                            width: 48, minWidth: 48, height: 'auto',
                            padding: 2, border: '1px solid #ccc', borderRadius: 4,
                            cursor: 'pointer', background: '#fff',
                          }}
                        />
                        <input
                          type="text"
                          value={periodicoForm.primary}
                          onChange={e => setPeriodicoForm({ ...periodicoForm, primary: e.target.value })}
                          placeholder="#1B4332"
                          maxLength={7}
                          style={{ flex: 1, fontFamily: 'ui-monospace, monospace' }}
                        />
                      </div>
                    </label>
                    <label className={styles.field}>
                      <span>Color secundario</span>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                        <input
                          type="color"
                          value={/^#[0-9a-fA-F]{6}$/.test(periodicoForm.secondary)
                                 ? periodicoForm.secondary : '#D4A017'}
                          onChange={e => setPeriodicoForm({
                            ...periodicoForm, secondary: e.target.value.toUpperCase(),
                          })}
                          title="Elegir color"
                          style={{
                            width: 48, minWidth: 48, height: 'auto',
                            padding: 2, border: '1px solid #ccc', borderRadius: 4,
                            cursor: 'pointer', background: '#fff',
                          }}
                        />
                        <input
                          type="text"
                          value={periodicoForm.secondary}
                          onChange={e => setPeriodicoForm({ ...periodicoForm, secondary: e.target.value })}
                          placeholder="#D4A017"
                          maxLength={7}
                          style={{ flex: 1, fontFamily: 'ui-monospace, monospace' }}
                        />
                      </div>
                    </label>
                  </div>

                  {/* Preview visual de la paleta elegida */}
                  <div style={{
                    marginTop: 8, padding: 12, borderRadius: 6,
                    background: '#fafafa', border: '1px solid #eee',
                    display: 'flex', alignItems: 'center', gap: 12,
                    fontSize: 13, color: '#666',
                  }}>
                    <span>Vista previa:</span>
                    <div style={{
                      padding: '6px 14px', borderRadius: 4,
                      background: periodicoForm.primary || '#1B4332',
                      color: '#fff', fontWeight: 600, fontSize: 12,
                    }}>Primario</div>
                    <div style={{
                      padding: '6px 14px', borderRadius: 4,
                      background: periodicoForm.secondary || '#D4A017',
                      color: '#fff', fontWeight: 600, fontSize: 12,
                    }}>Secundario</div>
                  </div>

                  {periodicoMsg && (
                    <div className={periodicoMsg.ok ? styles.msgOk : styles.msgErr}>
                      {periodicoMsg.text}
                    </div>
                  )}

                  <div style={{ marginTop: 20 }}>
                    <button
                      type="submit"
                      className={styles.addBtn}
                      disabled={periodicoSaving}
                    >
                      {periodicoSaving ? 'Guardando…' : 'Guardar cambios'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Parámetros editoriales — periodicidad, formato, timezone,
                aprobación, mantenimiento */}
            {activeTab === 'editorial' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Parámetros editoriales</h1>
                </div>

                {!editorialCfg ? (
                  <p>Cargando…</p>
                ) : (
                  <div style={{ maxWidth: 720 }}>
                    <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>
                      Configuración que rige el comportamiento del sistema.
                      Los cambios toman efecto inmediatamente al guardar.
                    </p>

                    {/* Publicación */}
                    <section style={{ marginBottom: 32, padding: 16, background: '#fafafa', borderRadius: 6 }}>
                      <h3 style={{ marginTop: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: '.05em', color: '#666' }}>
                        Publicación
                      </h3>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
                        <input
                          type="checkbox"
                          checked={!!editorialCfg.requiereAprobacion}
                          onChange={(e) => setEditorialCfg({ ...editorialCfg, requiereAprobacion: e.target.checked })}
                          style={{ marginTop: 4 }}
                        />
                        <div>
                          <strong>Requiere aprobación del Editor para publicar</strong>
                          <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                            Si está activado, los usuarios con rol <em>Redactor</em> solo pueden
                            marcar sus artículos como “En revisión”. Un <em>Editor</em> debe aprobarlos
                            desde la sección <strong>Revisiones pendientes</strong> para que se publiquen.
                          </div>
                        </div>
                      </label>
                    </section>

                    {/* Numeración y ritmo */}
                    <section style={{ marginBottom: 32 }}>
                      <h3 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '.05em', color: '#666' }}>
                        Ediciones
                      </h3>

                      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
                        <label>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                            Periodicidad
                          </span>
                          <select
                            value={editorialCfg.periodicidad}
                            onChange={(e) => setEditorialCfg({ ...editorialCfg, periodicidad: e.target.value })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                          >
                            {PERIODICIDADES.map(p => (
                              <option key={p.value} value={p.value}>{p.label}</option>
                            ))}
                          </select>
                        </label>

                        <label>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                            Formato de numeración
                          </span>
                          <select
                            value={editorialCfg.formatoNumeracion}
                            onChange={(e) => setEditorialCfg({ ...editorialCfg, formatoNumeracion: e.target.value })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                          >
                            {FORMATOS_NUM.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </section>

                    {/* Regional */}
                    <section style={{ marginBottom: 32 }}>
                      <h3 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '.05em', color: '#666' }}>
                        Regional
                      </h3>
                      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
                        <label>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                            Zona horaria
                          </span>
                          <select
                            value={editorialCfg.zonaHoraria}
                            onChange={(e) => setEditorialCfg({ ...editorialCfg, zonaHoraria: e.target.value })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                          >
                            {ZONAS_HORARIAS.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                          </select>
                        </label>
                        <label>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                            Idioma
                          </span>
                          <input
                            type="text"
                            value={editorialCfg.idioma || ''}
                            onChange={(e) => setEditorialCfg({ ...editorialCfg, idioma: e.target.value })}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                            placeholder="es-CO"
                          />
                        </label>
                      </div>
                    </section>

                    {/* Director del periódico — cargo público editorial,
                        no rol técnico. Se referencia a un autor existente. */}
                    <section style={{ marginBottom: 32 }}>
                      <h3 style={{ fontSize: 14, textTransform: 'uppercase', letterSpacing: '.05em', color: '#666' }}>
                        Director del periódico
                      </h3>
                      <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
                        Cargo editorial visible al lector (footer, sección "Nosotros").
                        No otorga permisos adicionales — la persona-directora normalmente
                        tiene rol <em>Editor</em> para operar el CMS. Se elige de la
                        lista de autores existentes; si aún no existe, créalo primero
                        en el tab <strong>Autores</strong>.
                      </p>

                      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                        {/* Preview del Director actualmente seleccionado */}
                        {editorialCfg.directorAutorId && (() => {
                          const dir = authors.find(a => a.id === editorialCfg.directorAutorId);
                          if (!dir) return null;
                          return (
                            <div style={{
                              width: 96, height: 96, borderRadius: '50%',
                              background: '#f0f0f0', overflow: 'hidden',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              flexShrink: 0, border: '2px solid var(--color-accent, #c9a227)',
                            }}>
                              {dir.avatar ? (
                                <img
                                  src={dir.avatar}
                                  alt={dir.name}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : (
                                <span style={{ fontSize: 32, color: '#bbb', fontFamily: 'var(--font-serif)' }}>
                                  {dir.name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              )}
                            </div>
                          );
                        })()}

                        <div style={{ flex: 1 }}>
                          <label>
                            <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                              Autor Director
                            </span>
                            <select
                              value={editorialCfg.directorAutorId || ''}
                              onChange={(e) => {
                                const value = e.target.value || null;
                                setEditorialCfg({
                                  ...editorialCfg,
                                  directorAutorId: value,
                                  // Bandera para desasignar cuando queda vacío
                                  limpiarDirector: value === null,
                                });
                              }}
                              style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                            >
                              <option value="">— Sin Director asignado —</option>
                              {(authors || []).map(a => (
                                <option key={a.id} value={a.id}>
                                  {a.name}{a.role ? ` · ${a.role}` : ''}
                                </option>
                              ))}
                            </select>
                          </label>
                          {editorialCfg.directorAutorId && (() => {
                            const dir = authors.find(a => a.id === editorialCfg.directorAutorId);
                            if (!dir) return null;
                            return (
                              <div style={{ marginTop: 8, fontSize: 13, color: '#666' }}>
                                <div><strong>{dir.name}</strong>{dir.role && <> · {dir.role}</>}</div>
                                {dir.bio && (
                                  <div style={{ marginTop: 4 }}>
                                    {dir.bio.length > 140 ? dir.bio.substring(0, 140) + '…' : dir.bio}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </section>

                    {/* Modo mantenimiento */}
                    <section style={{ marginBottom: 32, padding: 16, background: '#fff5e6', borderRadius: 6, border: '1px solid #f5c78e' }}>
                      <h3 style={{ marginTop: 0, fontSize: 14, textTransform: 'uppercase', letterSpacing: '.05em', color: '#a15c00' }}>
                        ⚠ Modo mantenimiento
                      </h3>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
                        <input
                          type="checkbox"
                          checked={!!editorialCfg.modoMantenimiento}
                          onChange={(e) => setEditorialCfg({ ...editorialCfg, modoMantenimiento: e.target.checked })}
                          style={{ marginTop: 4 }}
                        />
                        <div>
                          <strong>Activar modo mantenimiento</strong>
                          <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                            El sitio público responderá con un mensaje de mantenimiento
                            hasta que se desactive. El panel <code>/admin</code> sigue accesible.
                          </div>
                        </div>
                      </label>
                      {editorialCfg.modoMantenimiento && (
                        <label style={{ display: 'block', marginTop: 12 }}>
                          <span style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                            Mensaje mostrado al lector
                          </span>
                          <textarea
                            value={editorialCfg.mensajeMantenimiento || ''}
                            onChange={(e) => setEditorialCfg({ ...editorialCfg, mensajeMantenimiento: e.target.value })}
                            rows={3}
                            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontFamily: 'inherit' }}
                            placeholder="Estamos actualizando el sitio. Volvemos en unos minutos."
                          />
                        </label>
                      )}
                    </section>

                    {editorialMsg && (
                      <div className={editorialMsg.ok ? styles.msgOk : styles.msgErr}>
                        {editorialMsg.text}
                      </div>
                    )}

                    <div style={{ marginTop: 20 }}>
                      <button
                        onClick={saveEditorial}
                        className={styles.addBtn}
                        disabled={editorialSaving}
                      >
                        {editorialSaving ? 'Guardando…' : 'Guardar configuración'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Plantillas de página — editor independiente de layouts
                reutilizables para la maqueta impresa. */}
            {activeTab === 'plantillas' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Plantillas de página</h1>
                </div>
                <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                  Diseña layouts reutilizables de página para la versión impresa.
                  Estas plantillas se pueden aplicar desde el tab "Maquetación impresa"
                  de cualquier edición.
                </p>
                <PlantillasEditorPage />
              </div>
            )}

            {/* Revisiones pendientes — cola de aprobación (solo Editor) */}
            {activeTab === 'revisiones' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Revisiones pendientes</h1>
                </div>

                <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
                  Artículos en estado <strong>En revisión</strong> enviados por
                  Redactores esperando aprobación del Editor.
                </p>

                {pendientesLoading ? (
                  <p>Cargando…</p>
                ) : pendientes.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', background: '#f9f9f9', borderRadius: 6 }}>
                    <CheckSquare size={40} style={{ color: '#0a7d3b', marginBottom: 8 }} />
                    <p style={{ margin: 0, color: '#666' }}>No hay artículos pendientes de revisión.</p>
                  </div>
                ) : (
                  <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Título</th>
                          <th>Categoría</th>
                          <th>Autor</th>
                          <th style={{ textAlign: 'right' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendientes.map(art => (
                          <tr key={art.id}>
                            <td>
                              <strong>{art.title}</strong>
                              {art.excerpt && (
                                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                  {art.excerpt.length > 100 ? art.excerpt.substring(0, 100) + '…' : art.excerpt}
                                </div>
                              )}
                            </td>
                            <td>{art.category?.name || '—'}</td>
                            <td>{art.author?.name || '—'}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              <Link
                                to={`/admin/articulo/${art.id}`}
                                className={styles.iconBtn}
                                style={{ marginRight: 4 }}
                                title="Ver / editar"
                              >
                                <Eye size={14} />
                              </Link>
                              <button
                                className={styles.iconBtn}
                                onClick={() => aprobarArticulo(art.id)}
                                title="Aprobar y publicar"
                                style={{ marginRight: 4, color: '#0a7d3b' }}
                              >
                                Aprobar
                              </button>
                              <button
                                className={styles.iconBtn}
                                onClick={() => rechazarArticulo(art.id)}
                                title="Devolver a borrador"
                                style={{ color: '#b00020' }}
                              >
                                Rechazar
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Asistente virtual — chatbot basado en la KB del manual */}
            {activeTab === 'asistente' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Asistente virtual</h1>
                </div>
                <AssistantTab />
              </div>
            )}

            {/* Biblioteca Multimedia — material editorial */}
            {activeTab === 'biblioteca' && (
              <div className={styles.section}>
                <BibliotecaMultimediaPage />
              </div>
            )}

            {/* Publicidad — tarifario, anunciantes, pautas, cotizador */}
            {activeTab === 'publicidad' && (
              <div className={styles.section}>
                <PublicidadTab />
              </div>
            )}

            {/* Ayuda y reglas — documentación del sistema */}
            {activeTab === 'ayuda' && (
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <h1>Ayuda y reglas del sistema</h1>
                </div>

                <div style={{ maxWidth: 800, lineHeight: 1.6 }}>

                  <section style={{ marginBottom: 32 }}>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, marginBottom: 8 }}>
                      Estados de un artículo
                    </h2>
                    <p style={{ color: '#666', marginBottom: 12 }}>
                      Todo artículo pasa por uno de estos estados. Solo los publicados aparecen en el sitio.
                    </p>
                    <ul style={{ paddingLeft: 20 }}>
                      <li><strong>Borrador</strong>: el autor lo está escribiendo. No visible al público.</li>
                      <li><strong>En revisión</strong>: enviado por un Redactor. Esperando aprobación del Editor. No visible al público.</li>
                      <li><strong>Publicado</strong>: visible en el sitio, en su sección, en la home si es destacado, y en la página del autor.</li>
                      <li><strong>Archivado</strong>: retirado del sitio pero conservado en la BD para históricos. No visible.</li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: 32 }}>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, marginBottom: 8 }}>
                      Cómo se autoriza a un usuario en News
                    </h2>
                    <p style={{ marginBottom: 12 }}>
                      El acceso al portal se maneja en <strong>dos capas</strong>:
                    </p>
                    <ol style={{ paddingLeft: 20 }}>
                      <li style={{ marginBottom: 8 }}>
                        <strong>Autorización a la app (Ecolab):</strong> el administrador
                        de Ecolab, en su panel, autoriza qué apps del ecosistema puede
                        usar cada persona. Cuando le da acceso a <em>News</em>, el próximo
                        token JWT del usuario ya incluye <code>news</code> en el claim{' '}
                        <code>aud</code> y podrá pasar la autenticación del backend.
                      </li>
                      <li style={{ marginBottom: 8 }}>
                        <strong>Rol interno en News:</strong> una vez que la persona
                        entra al portal, aparece en el tab <strong>Usuarios y roles</strong>{' '}
                        de esta administración. Ahí, un <em>Editor</em> de News le asigna
                        uno o varios roles internos (<em>Editor</em>, <em>Redactor</em>,{' '}
                        <em>Colaborador</em>, <em>SoloLectura</em>). Sin al menos un rol
                        interno, la persona no puede ver el panel de administración.
                      </li>
                    </ol>
                    <p style={{ background: '#fff5e6', padding: 12, borderRadius: 6, fontSize: 13, color: '#a15c00' }}>
                      <strong>News NO crea usuarios.</strong> La identidad y el acceso
                      cross-app se gestionan en Ecolab. Desde News solo se administran
                      los roles internos.
                    </p>
                  </section>

                  <section style={{ marginBottom: 32 }}>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, marginBottom: 8 }}>
                      Roles y permisos
                    </h2>
                    <p style={{ color: '#666', marginBottom: 12 }}>
                      Cada usuario del ecosistema puede tener uno o varios roles en News.
                      La escalera va de menos a más permisos.
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                      <thead>
                        <tr style={{ background: '#f5f5f5' }}>
                          <th style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #ddd' }}>Rol</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #ddd' }}>Qué puede hacer</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 600 }}>SoloLectura</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>Solo ver el admin. No puede modificar nada.</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 600 }}>Colaborador</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>
                            Crear artículos en borrador. No puede publicar. Puede
                            tener scope regional (agrupación de equipo).
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 600 }}>Redactor</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>
                            Todo lo de Colaborador + editar cualquier artículo + enviar a revisión.
                            Si el periódico requiere aprobación, <strong>no puede publicar</strong>.
                            Puede tener scope regional.
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 600 }}>Editor</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd' }}>
                            Publica + aprueba/rechaza revisiones + gestiona autores.
                            Ediciones: <strong>solo de los departamentos asignados</strong>.
                            NO puede tocar la edición Nacional. NO puede asignar roles
                            Editor ni EditorNacional.
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd', fontWeight: 600, background: '#fff5e6' }}>
                            EditorNacional
                          </td>
                          <td style={{ padding: '8px 12px', border: '1px solid #ddd', background: '#fff5e6' }}>
                            <strong>Máximo nivel.</strong> Sin restricción de departamento —
                            opera sobre toda Colombia. Único que puede crear/tocar la
                            edición Nacional. Único que asigna roles (incluidos otros
                            Editores) y gestiona los departamentos asignados a cada usuario.
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </section>

                  <section style={{ marginBottom: 32 }}>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, marginBottom: 8 }}>
                      Ediciones regionales y nacional
                    </h2>
                    <ul style={{ paddingLeft: 20 }}>
                      <li>
                        Cada edición está asociada a un <strong>departamento</strong> (por su
                        municipio) o marcada como <strong>Nacional</strong> (una sola a la vez).
                      </li>
                      <li>
                        Un <em>Editor regional</em> solo ve y arma las ediciones de sus
                        departamentos asignados. Si no tiene departamentos, no ve ninguna.
                      </li>
                      <li>
                        Solo el <em>EditorNacional</em> puede crear, editar o publicar la
                        edición Nacional.
                      </li>
                      <li>
                        <strong>Fallback al lector:</strong> cuando alguien entra al sitio
                        y su departamento no tiene edición publicada, se le sirve la Nacional
                        con un aviso claro ("Tu región aún no tiene edición propia").
                      </li>
                      <li>
                        La <strong>home muestra solo los artículos vinculados a la edición
                        vigente</strong> (regional o nacional por fallback). Los artículos no
                        vinculados a ninguna edición vigente siguen accesibles por su sección
                        (categoría).
                      </li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: 32 }}>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, marginBottom: 8 }}>
                      Artículos vs Ediciones
                    </h2>
                    <p style={{ marginBottom: 12 }}>
                      Son dos entidades <strong>independientes</strong> pero relacionadas.
                    </p>
                    <ul style={{ paddingLeft: 20 }}>
                      <li>
                        <strong>Los artículos</strong> son la unidad principal. Se publican en su
                        sección al momento y aparecen en el sitio sin necesidad de estar en ninguna edición.
                      </li>
                      <li>
                        <strong>Las ediciones</strong> son un contenedor especial (típicamente
                        el "número" del periódico impreso, con portada y PDF) que agrupa un
                        conjunto de artículos. Sirven para archivo histórico y para regionalización
                        (una edición por departamento + una nacional).
                      </li>
                      <li>
                        <strong>No es obligatorio</strong> tener ediciones para que el sitio funcione.
                        Los artículos publican solos.
                      </li>
                      <li>
                        Cada edición tiene una <strong>fecha</strong> y puede haber múltiples ediciones
                        para el mismo departamento en distintas fechas. La "edición actual" es la más
                        reciente publicada.
                      </li>
                      <li>
                        Solo puede haber <strong>una edición nacional a la vez</strong>. Es el fallback
                        cuando el sistema no detecta ubicación o no hay edición para el departamento del lector.
                      </li>
                    </ul>
                  </section>

                  <section style={{ marginBottom: 32 }}>
                    <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, marginBottom: 8 }}>
                      Reglas del sistema
                    </h2>
                    <ul style={{ paddingLeft: 20 }}>
                      <li>Los artículos en <strong>borrador</strong> o <strong>en revisión</strong> nunca aparecen en el sitio público.</li>
                      <li>Todo artículo debe tener <strong>al menos un autor</strong>. Si no se elige, el sistema rechaza el guardado.</li>
                      <li>Al eliminar un autor, sus artículos <strong>pierden la firma</strong> pero no se borran.</li>
                      <li>La barra roja de <strong>"Última hora"</strong> solo muestra los artículos marcados con esa bandera y publicados.</li>
                      <li>En la <strong>home</strong> solo aparecen los artículos marcados como <em>destacados</em>.</li>
                      <li>Solo puede haber <strong>una edición nacional</strong> a la vez.</li>
                      <li>Solo puede haber <strong>un periódico por defecto</strong> del sistema multi-tenant.</li>
                      <li>Si el <strong>modo mantenimiento</strong> está activo, el sitio público responde con mensaje pero el admin sigue funcionando.</li>
                    </ul>
                  </section>

                  <section style={{ padding: 16, background: '#f0f7ff', borderRadius: 6, border: '1px solid #b8daff' }}>
                    <p style={{ margin: 0, fontSize: 14, color: '#004085' }}>
                      Esta sección se mantiene actualizada con el comportamiento real
                      del sistema. Si algo no funciona como aquí se describe, es un bug —
                      repórtalo.
                    </p>
                  </section>

                </div>
              </div>
            )}
          </>
        )}
      </main>

      {/* Modal: ediciones donde aparece un artículo. Se abre desde el
          botón de periódico en el listado del tab Artículos. */}
      {articleForEditions && (
        <div
          onClick={closeArticleEditions}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9998,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8,
              width: 'min(720px, 92vw)', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{
              padding: '14px 20px',
              borderBottom: '1px solid #e0e0e0',
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'var(--color-primary, #1B4332)', color: '#fff',
              borderTopLeftRadius: 8, borderTopRightRadius: 8,
            }}>
              <Newspaper size={20} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Ediciones del artículo</div>
                <div style={{
                  fontSize: 12, opacity: 0.85,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{articleForEditions.title}</div>
              </div>
              <button
                onClick={closeArticleEditions}
                style={{
                  background: 'transparent', border: 'none', color: '#fff',
                  cursor: 'pointer', padding: 6,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                title="Cerrar"
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
              {loadingEditions ? (
                <p style={{ color: '#666', textAlign: 'center' }}>Cargando…</p>
              ) : articleEditions.length === 0 ? (
                <p style={{ color: '#666', textAlign: 'center', padding: 24 }}>
                  Este artículo no está vinculado a ninguna edición.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {articleEditions.map(ed => (
                    <li key={ed.editionId} style={{
                      padding: '12px 14px',
                      marginBottom: 8,
                      border: '1px solid #e0e0e0',
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', gap: 12,
                      background: '#fff',
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        }}>
                          <strong style={{ fontSize: 14, color: '#1B4332' }}>
                            Edición #{ed.number}
                          </strong>
                          {ed.title && (
                            <span style={{ fontSize: 13, color: '#333' }}>· {ed.title}</span>
                          )}
                        </div>
                        <div style={{
                          display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap',
                        }}>
                          {/* Estado de la edición */}
                          <span style={{
                            padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                            background: ed.status === 'published' ? '#d1fae5' : '#f3f4f6',
                            color:      ed.status === 'published' ? '#065f46' : '#6b7280',
                          }}>
                            {ed.status === 'published' ? 'Publicada' : ed.status === 'draft' ? 'Borrador' : ed.status}
                          </span>
                          {/* Portada dentro de esta edición */}
                          {ed.isCover && (
                            <span style={{
                              padding: '2px 10px', borderRadius: 999,
                              background: '#c9a227', color: '#1a1a1a',
                              fontSize: 11, fontWeight: 700, letterSpacing: '.03em',
                            }}>⭐ PORTADA</span>
                          )}
                          {/* Nacional vs regional */}
                          {ed.isDefault ? (
                            <span style={{
                              padding: '2px 10px', borderRadius: 999,
                              background: '#dbeafe', color: '#1e40af',
                              fontSize: 11, fontWeight: 600,
                            }}>Nacional</span>
                          ) : ed.departmentName ? (
                            <span style={{
                              padding: '2px 10px', borderRadius: 999,
                              background: '#f3f4f6', color: '#374151',
                              fontSize: 11,
                            }}>{ed.departmentName}{ed.municipalityName ? ` · ${ed.municipalityName}` : ''}</span>
                          ) : null}
                          {/* Fecha editorial */}
                          <span style={{ fontSize: 11, color: '#666' }}>
                            📅 {ed.date ? new Date(ed.date).toLocaleDateString() : '—'}
                          </span>
                        </div>
                      </div>
                      <Link
                        to={`/admin/edicion/editar/${ed.editionId}`}
                        onClick={closeArticleEditions}
                        style={{
                          padding: '6px 12px',
                          background: 'var(--color-primary, #1B4332)', color: '#fff',
                          borderRadius: 4, textDecoration: 'none',
                          fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        <Edit size={13} /> Editar
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle usuario (roles activos + asignar/revocar + historial) */}
      {selectedUser && (
        <div
          onClick={closeUserModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              width: '100%',
              maxWidth: 640,
              maxHeight: '85vh',
              overflow: 'auto',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontFamily: 'var(--font-serif)' }}>
                  {selectedUser.nombre} {selectedUser.apellido}
                </h2>
                <p style={{ color: '#666', margin: '4px 0 0 0' }}>{selectedUser.email}</p>
              </div>
              <button
                onClick={closeUserModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
              >
                <X size={20} />
              </button>
            </div>

            <section style={{ marginBottom: 24 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666', margin: '0 0 8px 0' }}>
                <Users size={14} /> Autor asociado
              </h3>
              {(() => {
                // Buscar autor vinculado a este usuario
                const linkedAuthor = (authors || []).find(a => a.userId === selectedUser.id);
                if (linkedAuthor) {
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                      <div>
                        <strong>{linkedAuthor.name}</strong>
                        {linkedAuthor.email && (
                          <small style={{ color: '#666', marginLeft: 8 }}>({linkedAuthor.email})</small>
                        )}
                      </div>
                      <button
                        onClick={() => unlinkUserFromAuthor(linkedAuthor)}
                        disabled={userSaving || authorSaving}
                        style={{
                          padding: '4px 10px',
                          border: '1px solid #b00020',
                          background: '#fff',
                          color: '#b00020',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: 12,
                        }}
                      >
                        Desvincular
                      </button>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                    <span style={{ color: '#666', fontSize: 14 }}>
                      Sin autor asociado. El usuario no puede firmar artículos aún.
                    </span>
                    <button
                      onClick={createAuthorFromUser}
                      disabled={userSaving}
                      className={styles.addBtn}
                      style={{ fontSize: 13 }}
                    >
                      <Plus size={14} /> Crear autor
                    </button>
                  </div>
                );
              })()}
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666', margin: '0 0 8px 0' }}>
                <Shield size={14} /> Roles activos
              </h3>
              {(selectedUser.roles || []).length === 0 && (
                <p style={{ color: '#666' }}>Sin roles asignados en News.</p>
              )}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {(selectedUser.roles || []).map(r => (
                  <li key={r.rol} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    borderBottom: '1px dashed #eee',
                  }}>
                    <div>
                      <strong>{r.rol}</strong>
                      <small style={{ color: '#666', marginLeft: 8 }}>
                        asignado {r.asignadoEn ? new Date(r.asignadoEn).toLocaleString() : '—'}
                        {r.asignadoPorNombre && ` por ${r.asignadoPorNombre}`}
                      </small>
                    </div>
                    <button
                      onClick={() => revokeRole(r.rol)}
                      disabled={userSaving}
                      style={{
                        padding: '4px 10px',
                        border: '1px solid #b00020',
                        background: '#fff',
                        color: '#b00020',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      Revocar
                    </button>
                  </li>
                ))}
              </ul>

              <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' }}>
                <select
                  value={roleToAssign}
                  onChange={(e) => setRoleToAssign(e.target.value)}
                  disabled={userSaving}
                  style={{ flex: 1, padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                >
                  {ROLES_DISPONIBLES
                    .filter(r => !(selectedUser.roles || []).some(x => x.rol === r))
                    .map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <button
                  onClick={assignRole}
                  disabled={userSaving || (selectedUser.roles || []).some(x => x.rol === roleToAssign)}
                  className={styles.addBtn}
                >
                  <Plus size={16} /> Asignar rol
                </button>
              </div>
            </section>

            {/* Departamentos asignados (scope regional). Aplica a
                Editor, Redactor y Colaborador. EditorNacional no lo
                necesita, pero mostramos la sección informativa. */}
            <section style={{ marginBottom: 24 }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666', margin: '0 0 8px 0' }}>
                <Newspaper size={14} /> Departamentos asignados
              </h3>

              {(selectedUser.roles || []).some(r => r.rol === 'EditorNacional') ? (
                <p style={{ color: '#666', fontStyle: 'italic', margin: '8px 0' }}>
                  Este usuario es <strong>EditorNacional</strong>: opera sobre todo el
                  territorio sin restricción de departamento.
                </p>
              ) : (
                <>
                  {userRegions.length === 0 && (
                    <p style={{ color: '#666', margin: '8px 0' }}>
                      Sin departamentos asignados. Un <em>Editor</em> sin
                      departamentos no puede ver ni operar ninguna edición.
                    </p>
                  )}

                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {userRegions.map(r => (
                      <li key={r.departamentoId} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px dashed #eee',
                      }}>
                        <div>
                          <strong>{r.departamentoNombre || `Depto ${r.departamentoId}`}</strong>
                          <small style={{ color: '#666', marginLeft: 8 }}>
                            asignado {r.asignadoEn ? new Date(r.asignadoEn).toLocaleDateString() : '—'}
                            {r.asignadoPorNombre && ` por ${r.asignadoPorNombre}`}
                          </small>
                        </div>
                        <button
                          onClick={() => revokeRegion(r.departamentoId, r.departamentoNombre)}
                          disabled={regionSaving}
                          style={{
                            padding: '4px 10px',
                            border: '1px solid #b00020',
                            background: '#fff',
                            color: '#b00020',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          Quitar
                        </button>
                      </li>
                    ))}
                  </ul>

                  <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid #eee' }}>
                    <select
                      value={regionToAssign}
                      onChange={(e) => setRegionToAssign(e.target.value)}
                      disabled={regionSaving}
                      style={{ flex: 1, padding: '6px 10px', border: '1px solid #ccc', borderRadius: 4 }}
                    >
                      <option value="">— Elige un departamento —</option>
                      {(departamentos || [])
                        .filter(d => !userRegions.some(r => r.departamentoId === d.id))
                        .map(d => (
                          <option key={d.id} value={d.id}>{d.nombre}</option>
                        ))}
                    </select>
                    <button
                      onClick={assignRegion}
                      disabled={regionSaving || !regionToAssign}
                      className={styles.addBtn}
                    >
                      <Plus size={16} /> Asignar
                    </button>
                  </div>
                </>
              )}
            </section>

            <section>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#666', margin: '0 0 8px 0' }}>
                <Clock size={14} /> Historial reciente
              </h3>
              {userHistory.length === 0 && (
                <p style={{ color: '#666' }}>Sin eventos registrados.</p>
              )}
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {userHistory.map((h, i) => (
                  <li key={i} style={{
                    padding: '6px 0',
                    fontSize: 14,
                    borderBottom: '1px dashed #eee',
                  }}>
                    <strong>{h.accion === 'asignar' ? 'Asignó' : 'Revocó'}</strong>
                    {' '}{h.rol}
                    {' · '}
                    <span style={{ color: '#666' }}>
                      {new Date(h.fecha).toLocaleString()}
                    </span>
                    {h.ejecutorNombre && <> por <em>{h.ejecutorNombre}</em></>}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        </div>
      )}
      {/* Modal: vincular un usuario a un autor existente */}
      {linkingAuthor && (
        <div
          onClick={closeLinkUserModal}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              width: '100%',
              maxWidth: 540,
              maxHeight: '80vh',
              overflow: 'auto',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontFamily: 'var(--font-serif)' }}>
                  Vincular usuario
                </h2>
                <p style={{ color: '#666', margin: '4px 0 0 0', fontSize: 14 }}>
                  Autor: <strong>{linkingAuthor.name}</strong>
                </p>
              </div>
              <button
                onClick={closeLinkUserModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
              >
                <X size={20} />
              </button>
            </div>

            <input
              type="text"
              placeholder="Filtrar por nombre o email..."
              value={candidateSearch}
              onChange={(e) => setCandidateSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: 4,
                marginBottom: 12,
                fontSize: 14,
              }}
            />

            {candidateUsers.length === 0 ? (
              <p style={{ color: '#666' }}>
                No hay usuarios con roles asignados. Asigna un rol primero a algún usuario.
              </p>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {candidateUsers
                  .filter(u => {
                    const s = candidateSearch.toLowerCase();
                    if (!s) return true;
                    return (u.nombre || '').toLowerCase().includes(s)
                        || (u.email  || '').toLowerCase().includes(s);
                  })
                  .map(u => {
                    const alreadyLinked = (authors || []).some(a => a.userId === u.id);
                    return (
                      <li key={u.id} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 0',
                        borderBottom: '1px dashed #eee',
                      }}>
                        <div>
                          <strong>{u.nombre} {u.apellido}</strong>
                          <small style={{ color: '#666', marginLeft: 8 }}>{u.email}</small>
                          {alreadyLinked && (
                            <div style={{ color: '#b00020', fontSize: 11, marginTop: 2 }}>
                              Ya está vinculado a otro autor
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => linkUserToAuthor(u.id)}
                          disabled={authorSaving || alreadyLinked}
                          className={styles.addBtn}
                          style={{ fontSize: 13, opacity: alreadyLinked ? 0.4 : 1 }}
                        >
                          Vincular
                        </button>
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
        </div>
      )}
      {/* Modal: crear / editar autor */}
      {editingAuthor && (
        <div
          onClick={closeAuthorEditor}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 8,
              width: '100%',
              maxWidth: 640,
              maxHeight: '85vh',
              overflow: 'auto',
              padding: 24,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontFamily: 'var(--font-serif)' }}>
                {editingAuthor.id ? 'Editar autor' : 'Nuevo autor'}
              </h2>
              <button
                onClick={closeAuthorEditor}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
              >
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'block' }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Nombre <span style={{ color: '#b00020' }}>*</span>
                </span>
                <input
                  type="text"
                  value={editingAuthor.name || ''}
                  onChange={(e) => setAuthorField('name', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                  placeholder="Ej: María Rodríguez"
                />
              </label>

              <label style={{ display: 'block' }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Slug
                  <small style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                    (URL pública: /autor/{editingAuthor.slug || 'slug-del-autor'})
                  </small>
                </span>
                <input
                  type="text"
                  value={editingAuthor.slug || ''}
                  onChange={(e) => setAuthorField('slug', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                  placeholder="maria-rodriguez"
                />
              </label>

              <label style={{ display: 'block' }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Biografía
                </span>
                <textarea
                  value={editingAuthor.bio || ''}
                  onChange={(e) => setAuthorField('bio', e.target.value)}
                  rows={4}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14, fontFamily: 'inherit' }}
                  placeholder="Perfil profesional y trayectoria del autor..."
                />
              </label>

              <div>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                  Foto del autor
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  {/* Preview: primero el archivo elegido, si no la foto guardada */}
                  <div style={{
                    width: 96, height: 96, borderRadius: '50%',
                    background: '#f0f0f0', overflow: 'hidden',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, border: '2px solid #e5e5e5',
                  }}>
                    {(avatarPreview || editingAuthor.avatar) ? (
                      <img
                        src={avatarPreview || editingAuthor.avatar}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <span style={{ fontSize: 32, color: '#bbb', fontFamily: 'var(--font-serif)' }}>
                        {(editingAuthor.name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px',
                      border: '1px solid #ccc', borderRadius: 4,
                      background: '#fff', cursor: 'pointer', fontSize: 14,
                    }}>
                      <Camera size={16} />
                      {avatarFile ? 'Cambiar imagen' : 'Subir imagen'}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={onAvatarFileChange}
                        style={{ display: 'none' }}
                      />
                    </label>
                    {avatarFile && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                        {avatarFile.name} ({Math.round(avatarFile.size / 1024)} KB)
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                      Desde tu celular puedes tomar la foto directamente.
                      Máx. 5 MB. JPG, PNG o WebP.
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Email público *
                  </span>
                  <input
                    type="email"
                    value={editingAuthor.email || ''}
                    onChange={(e) => setAuthorField('email', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    placeholder="autor@ejemplo.com"
                    required
                  />
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Teléfono celular *
                  </span>
                  <input
                    type="tel"
                    value={editingAuthor.celular || ''}
                    onChange={(e) => setAuthorField('celular', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    placeholder="+57 300 000 0000"
                    required
                  />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <label>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Twitter / X
                  </span>
                  <input
                    type="text"
                    value={editingAuthor.twitter || ''}
                    onChange={(e) => setAuthorField('twitter', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    placeholder="@usuario"
                  />
                </label>
                <label>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Instagram
                  </span>
                  <input
                    type="text"
                    value={editingAuthor.instagram || ''}
                    onChange={(e) => setAuthorField('instagram', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    placeholder="@usuario"
                  />
                </label>
                <label>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    LinkedIn
                  </span>
                  <input
                    type="text"
                    value={editingAuthor.linkedin || ''}
                    onChange={(e) => setAuthorField('linkedin', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    placeholder="in/usuario"
                  />
                </label>
                <label>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4 }}>
                    Sitio web
                  </span>
                  <input
                    type="url"
                    value={editingAuthor.website || ''}
                    onChange={(e) => setAuthorField('website', e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }}
                    placeholder="https://..."
                  />
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, paddingTop: 16, borderTop: '1px solid #eee' }}>
              <button
                onClick={closeAuthorEditor}
                disabled={authorSaving}
                style={{ padding: '8px 16px', border: '1px solid #ccc', background: '#fff', borderRadius: 4, cursor: 'pointer' }}
              >
                Cancelar
              </button>
              <button
                onClick={saveAuthor}
                disabled={authorSaving}
                className={styles.addBtn}
              >
                {authorSaving ? 'Guardando…' : (editingAuthor.id ? 'Guardar cambios' : 'Crear autor')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat flotante — visible en todo el admin. Arrastrable, con
          posición persistida en localStorage por el propio widget. */}
      <ChatWidget />
    </div>
  );
}
